'use client';

/**
 * ProofQuotes — the only testimonial section component on the site.
 *
 * Consumed directly by every page that needs proof:
 *   - / (home)            — 3 quotes, single metric
 *   - /features           — 3 quotes, single metric
 *   - /success-stories    — 12 quotes, chip metric, footnote
 *   - /retail, /services  — 3 quotes, token-chip metric, per-vertical accent
 *   - /events, /b2b       — 3 quotes, single metric, per-vertical accent
 *
 * Each consumer page builds the props inline. There are no per-page
 * wrapper components — copy lives next to the JSX in each `page.tsx`.
 *
 * The grid uses `repeat(auto-fit, minmax(280px, 1fr))` so it adapts to
 * the number of items: 1/2/3/4 fit on one row at desktop, 8 wraps to
 * 2×4, 12 wraps to 3×4. Mobile collapses to a single column.
 *
 * Three metric formats, discriminated by `Quote.metric`:
 *   - `string`              → muted `.sum-quote-metric` tag.
 *   - `string` w/ `chip`    → bordered cyan chip (used by success-stories).
 *   - `string[]`            → accent-colored pricetag chips joined by '+'.
 *
 * The accent color is per-section, not per-card. Defaults to cyan
 * (`#22D3EE`); pages can pass any accent for tinted chips.
 */

import React from 'react';
import { IoMdPricetag } from 'react-icons/io';
import AggregateRating from './AggregateRating';
import SectionLabel from '../../home/SectionLabel';

/** Hex like '#A78BFA' → 'r,g,b'. */
const hexToRgb = (hex: string): string => {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};

export type ProofQuote = {
  /** Stable React key. */
  key: string;
  quote: string;
  name: string;
  /** Single role string, OR a richer composite (e.g. business · region). */
  role: string;
  /**
   * Optional metric tag. Three formats supported:
   *   - string                              → muted single tag
   *   - { kind: 'chip', label: string }     → bordered accent chip (1 chip)
   *   - string[]                            → accent pricetag chips with '+'
   * Omit to render the card without any metric chip.
   */
  metric?: string | string[] | { kind: 'chip'; label: string };
};

type Props = {
  num: string;
  label: string;
  headlineA: string;
  headlineB: string;
  subhead?: string;
  quotes: ProofQuote[];
  /** Accent for tinted/chip metrics; defaults to V3 cyan. */
  accent?: string;
  /** Optional anchor id. */
  id?: string;
  /** Optional small-print paragraph rendered under the grid. */
  footnote?: string;
  /** Border-top hairline; useful when the prior section shares this background. */
  ruleTop?: boolean;
  /**
   * When true, the bottom of the grid fades into the section background — a
   * subtle "iceberg" cue for long lists (e.g. 12-quote /success-stories grid).
   * The grid itself stays fully rendered and scrollable past; this is purely
   * a visual treatment over the last row.
   */
  fadeBottom?: boolean;
  /**
   * Optional aggregate rating, rendered between the headline and the grid.
   * Must mirror the host page's `<script type="application/ld+json">`
   * `aggregateRating` block — Google's structured-data policy requires the
   * rendered DOM and the schema to agree.
   */
  aggregateRating?: {
    value: number;
    count: number;
    starsAriaLabel: string;
    basedOnLabel: string;
  };
};

const ProofQuotes = ({
  num,
  label,
  headlineA,
  headlineB,
  subhead,
  quotes,
  accent = '#22D3EE',
  id,
  footnote,
  ruleTop,
  fadeBottom,
  aggregateRating,
}: Props) => {
  const accentRgb = hexToRgb(accent);

  return (
    <section
      id={id}
      className={`px-6 ${fadeBottom ? 'pt-24 lg:pt-28 pb-10 lg:pb-12' : 'py-24 lg:py-28'}`}
      style={{
        background: '#0E1130',
        ...(ruleTop ? { borderTop: '1px solid rgba(255,255,255,0.05)' } : {}),
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={num} label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: subhead ? '12px 0 14px' : '12px 0 48px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        {subhead && (
          <p
            style={{
              color: '#B4C1D8',
              fontSize: 18,
              maxWidth: 720,
              lineHeight: 1.5,
              margin: aggregateRating ? '0 0 24px' : '0 0 48px',
            }}
          >
            {subhead}
          </p>
        )}

        {aggregateRating && (
          <AggregateRating
            value={aggregateRating.value}
            count={aggregateRating.count}
            starsAriaLabel={aggregateRating.starsAriaLabel}
            basedOnLabel={aggregateRating.basedOnLabel}
            accent={accent}
          />
        )}

        <div style={{ position: 'relative' }}>
          <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            {quotes.map((q) => {
              const isTokenList = Array.isArray(q.metric);
              const isStyledChip =
                !isTokenList &&
                typeof q.metric === 'object' &&
                q.metric !== null &&
                (q.metric as { kind?: string }).kind === 'chip';
              const stackMeta = isTokenList || isStyledChip;

              return (
                <figure key={q.key} className="sum-quote-card">
                  <blockquote className="sum-quote-text" style={{ fontSize: 15, lineHeight: 1.55 }}>
                    “{q.quote}”
                  </blockquote>
                  <figcaption
                    className="sum-quote-meta"
                    style={stackMeta ? { flexDirection: 'column', alignItems: 'stretch', gap: 12 } : undefined}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{q.name}</div>
                      <div style={{ color: '#B4C1D8', fontSize: 13 }}>{q.role}</div>
                    </div>

                    {typeof q.metric === 'string' && <div className="sum-quote-metric">{q.metric}</div>}

                    {isStyledChip && (
                      <div
                        className="inline-flex items-center self-start"
                        style={{
                          gap: 4,
                          background: `rgba(${accentRgb},0.08)`,
                          border: `1px solid rgba(${accentRgb},0.2)`,
                          borderRadius: 6,
                          padding: '4px 10px',
                          color: accent,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {(q.metric as { kind: 'chip'; label: string }).label}
                      </div>
                    )}

                    {isTokenList && (
                      <div
                        className="flex flex-wrap items-center"
                        style={{ gap: 4, rowGap: 6 }}
                        aria-label={(q.metric as string[]).join(' plus ')}
                      >
                        {(q.metric as string[]).map((token, i) => (
                          <React.Fragment key={token}>
                            {i > 0 && (
                              <span
                                aria-hidden="true"
                                style={{
                                  color: '#6B7593',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  padding: '0 1px',
                                }}
                              >
                                +
                              </span>
                            )}
                            <span
                              className="inline-flex items-center"
                              style={{
                                gap: 4,
                                background: `rgba(${accentRgb},0.1)`,
                                color: accent,
                                padding: '4px 8px',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                border: `1px solid rgba(${accentRgb},0.2)`,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <IoMdPricetag aria-hidden="true" style={{ fontSize: 11, flexShrink: 0 }} />
                              {token}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
          {fadeBottom && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 180,
                pointerEvents: 'none',
                background: 'linear-gradient(180deg, rgba(10,14,39,0) 0%, #0E1130 85%)',
                backdropFilter: 'blur(1.5px)',
                WebkitBackdropFilter: 'blur(1.5px)',
                maskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 60%)',
                WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 60%)',
              }}
            />
          )}
        </div>

        {footnote && (
          <p
            style={{
              color: '#6B7593',
              fontSize: 12,
              margin: '32px 0 0',
              maxWidth: 720,
              lineHeight: 1.5,
            }}
          >
            {footnote}
          </p>
        )}
      </div>
    </section>
  );
};

export default ProofQuotes;
